/**
 * Ready-made cases for the Inbox Reply pane.
 *
 * Writing a good case from scratch means knowing what an incoming reply
 * typically looks like and how the matcher reads it — knowledge a new user
 * doesn't have yet. These templates pre-fill a complete, working case for the
 * reply types the inbox actually sees, so the first case is a review-and-adjust
 * job rather than a blank page.
 *
 * The scenarios mirror the reply subtypes the mail analyzer classifies
 * (see src/shared/enums.js), which is what the mood range filters on.
 */

export const BLANK_AGENT = {
  name: '',
  case: '',
  task: '',
  type: 'Reply',
  happinessMin: 1,
  happinessMax: 10,
  useCalendly: false,
  selectedEventType: null,
  calendlyGuidelines: '',
  useTag: false,
  tag: '',
  tagColor: '#1e40af',
  tagIcon: '',
  tagDescription: '',
  selectedTopic: '',
  // How the answer is produced. 'instructions' is the historical behaviour (the
  // AI writes from `task`); 'template' sends `replyTemplate`, with `adherence`
  // deciding how far it may stray from it; 'saved' picks one of the user's own
  // templates and uses that template's own writer settings (aiAdherence,
  // formatting, language, …) — no extra slider on the case.
  replyMode: 'instructions',
  replyTemplate: '',
  // Reference into /World/{uid}/settings/customTopics — we store the RTDB key
  // (not the topic's display name), so renaming a template keeps the case wired.
  // savedTemplateVariantId '' means: pick a variant at random per email.
  savedTemplateId: '',
  savedTemplateVariantId: '',
  adherence: 'balanced',
  // Absolute rules. They win over everything else, in both modes.
  dos: [],
  donts: [],
  // Missing `enabled` on older cases still means on.
  enabled: true,
};

/**
 * How much the AI may depart from a fixed template. Same four levels and the
 * same keys the outbound template writer uses (see ADHERENCE_MAP in
 * functions/WorldEmailCreator.js), so "close" means the same thing everywhere.
 */
export const ADHERENCE_LEVELS = [
  {
    id: 'exact',
    label: 'Word for word',
    short: 'Fixed',
    description: 'Sent exactly as written. Nothing changes.',
  },
  {
    id: 'close',
    label: 'Light touch',
    short: 'Close',
    description: 'Your wording stays, only grammar is smoothed.',
  },
  {
    id: 'balanced',
    label: 'Adapts to the email',
    short: 'Balanced',
    description: 'Keeps your points, rewords them to fit the email.',
  },
  {
    id: 'loose',
    label: 'Free rewrite',
    short: 'Free',
    description: 'Uses your text as a briefing, writes its own answer.',
  },
];

/**
 * Each template carries the full agent payload plus the copy that explains it
 * in the gallery. `example` is a real-sounding reply so the user can recognise
 * the situation instead of decoding the trigger text.
 */
export const AGENT_TEMPLATES = [
  {
    id: 'interested-meeting',
    title: 'Interested → propose a call',
    blurb: 'Your best replies. Answers the question and offers a time straight away.',
    example: '“Sounds interesting. Can you tell me a bit more?”',
    icon: 'fa-handshake',
    accent: 'green',
    agent: {
      ...BLANK_AGENT,
      name: 'Interested',
      type: 'Reply',
      case: 'The sender replied positively: they are interested, curious, or want to know more about what we offer.',
      task: 'Thank them for the quick reply, answer their question in one or two sentences, and propose a short call to walk them through it. Stay warm, concrete and brief.',
      happinessMin: 6,
      happinessMax: 10,
      useCalendly: true,
      calendlyGuidelines: 'Offer two or three slots on different days and always name the time zone.',
      useTag: true,
      tag: 'interested',
      tagColor: '#22c55e',
      tagIcon: '/img/tag_eye.svg',
      tagDescription: 'Positive reply. Worth following up quickly.',
    },
  },
  {
    id: 'pricing',
    title: 'Asks about pricing',
    blurb: 'Answers the cost question without naming a fixed number too early.',
    example: '“What would something like this cost us?”',
    icon: 'fa-coins',
    accent: 'amber',
    agent: {
      ...BLANK_AGENT,
      name: 'Pricing question',
      type: 'Reply',
      case: 'The sender asks what it costs, requests a quote, or raises a budget concern.',
      task: 'Acknowledge the question directly, explain that the price depends on scope, name a realistic starting point, and offer a short call to put together an exact quote.',
      happinessMin: 3,
      happinessMax: 10,
      useTag: true,
      tag: 'pricing',
      tagColor: '#f59e0b',
      tagIcon: '/img/tag_coin.svg',
      tagDescription: 'Asked about cost or budget.',
    },
  },
  {
    id: 'more-info',
    title: 'Has a concrete question',
    blurb: 'Replies to specific questions about how it works or what is included.',
    example: '“How exactly does this work for a company our size?”',
    icon: 'fa-circle-question',
    accent: 'blue',
    agent: {
      ...BLANK_AGENT,
      name: 'Question about the offer',
      type: 'Reply',
      case: 'The sender asks a concrete question about how it works, what is included, or how it would apply to their business.',
      task: 'Answer the question directly and specifically, refer back to what they wrote, avoid generic marketing language, and close with one clear next step.',
      happinessMin: 4,
      happinessMax: 10,
    },
  },
  {
    id: 'wrong-person',
    title: 'Not the right contact',
    blurb: 'Turns a dead end into a referral to the person who does decide.',
    example: '“I’m not responsible for this, try our marketing team.”',
    icon: 'fa-user-group',
    accent: 'sky',
    agent: {
      ...BLANK_AGENT,
      name: 'Wrong contact',
      type: 'Reply',
      case: 'The sender says they are not responsible for this topic, or points to a colleague who is.',
      task: 'Thank them for taking the time, apologise briefly for reaching the wrong desk, and ask politely who the right contact would be.',
      happinessMin: 3,
      happinessMax: 10,
      useTag: true,
      tag: 'wrong contact',
      tagColor: '#6d28d9',
      tagIcon: '/img/tag_rotate.svg',
      tagDescription: 'Referred us to somebody else.',
    },
  },
  {
    id: 'later',
    title: 'Bad timing → follow up later',
    blurb: 'Keeps the door open and asks for a concrete month to come back.',
    example: '“Not right now, maybe next quarter.”',
    icon: 'fa-hourglass-half',
    accent: 'indigo',
    agent: {
      ...BLANK_AGENT,
      name: 'Follow up later',
      type: 'Reply',
      case: 'The sender is interested in principle but says the timing is wrong. No budget this quarter, a running project, or simply too busy.',
      task: 'Show understanding, do not push, and ask for a concrete month when it would make sense to get back in touch.',
      happinessMin: 3,
      happinessMax: 8,
      useTag: true,
      tag: 'follow up later',
      tagColor: '#1d4ed8',
      tagIcon: '/img/tag_time.svg',
      tagDescription: 'Interested, but not right now.',
    },
  },
  {
    id: 'not-interested',
    title: 'Not interested → stay silent',
    blurb: 'Marks the reply so you see it, but never answers a clear no.',
    example: '“Thanks, but we already have a solution.”',
    icon: 'fa-circle-minus',
    accent: 'slate',
    agent: {
      ...BLANK_AGENT,
      name: 'Not interested',
      type: 'No Reply',
      case: 'The sender clearly declines: no need, no fit, or they already work with someone else.',
      happinessMin: 1,
      happinessMax: 5,
      useTag: true,
      tag: 'not interested',
      tagColor: '#9f1239',
      tagIcon: '/img/tag_close.svg',
      tagDescription: 'Declined. Do not chase.',
    },
  },
  {
    id: 'do-not-contact',
    title: 'Unsubscribe or complaint',
    blurb: 'The one case where replying makes it worse. Flags it for you instead.',
    example: '“Remove me from your list immediately.”',
    icon: 'fa-triangle-exclamation',
    accent: 'rose',
    agent: {
      ...BLANK_AGENT,
      name: 'Do not contact',
      type: 'No Reply',
      case: 'The sender asks to be removed from the list, refers to GDPR or legal action, or is openly hostile.',
      happinessMin: 1,
      happinessMax: 3,
      useTag: true,
      tag: 'do not contact',
      tagColor: '#dc2626',
      tagIcon: '/img/tag_close.svg',
      tagDescription: 'Never email this contact again.',
    },
  },
  {
    id: 'auto-reply',
    title: 'Out of office → just tag',
    blurb: 'Keeps machine-generated mail out of your way without answering it.',
    example: '“I am out of office until the 14th.”',
    icon: 'fa-robot',
    accent: 'slate',
    agent: {
      ...BLANK_AGENT,
      name: 'Automatic message',
      type: 'Only Tag',
      case: 'A machine-generated message: out-of-office notice, holiday reply, delivery report or another system email.',
      happinessMin: 1,
      happinessMax: 10,
      useTag: true,
      tag: 'auto-reply',
      tagColor: '#84cc16',
      tagIcon: '/img/tag_rotate.svg',
      tagDescription: 'Automated message, no human behind it.',
    },
  },
];

/** Returns a fresh, unlinked copy so editing a draft can't mutate the library. */
export const templateToDraft = (template) => ({
  ...BLANK_AGENT,
  ...(template ? template.agent : null),
});

/**
 * Body variants of a saved email template. RTDB stores `topicSpecificNotes`
 * as an array or as an index-keyed object; older records still have the
 * singular `topicSpecificNote` string. Both shapes have to look the same
 * to the picker and the preview, or a valid template appears empty.
 */
export const topicBodyVariants = (topic) => {
  if (!topic) return [];
  const raw = topic.topicSpecificNotes;
  const variants = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === 'object' ? Object.values(raw) : []);
  const usable = variants.filter((v) => v && String(v.text || '').trim());
  if (usable.length) return usable;
  const legacy = String(topic.topicSpecificNote || '').trim();
  return legacy ? [{ id: 'legacy', text: legacy }] : [];
};

/** Named templates the reply case can pick from (custom + selected). */
export const listSavedReplyTemplates = (userTopics) => Object.entries(userTopics || {})
  .filter(([, topic]) => topic && String(topic.topic || '').trim())
  .sort((a, b) => (a[1].topic || '').localeCompare(b[1].topic || ''));

/** Trigger phrasings offered as one-click starters in the editor. */
export const TRIGGER_EXAMPLES = [
  'The sender wants a demo or a live walkthrough.',
  'The sender asks for references or case studies.',
  'The sender forwards us to a colleague.',
  'The sender complains about too many emails.',
  'The sender asks a technical question about the integration.',
];

/** Hard rules offered as one-click starters, phrased as absolutes. */
export const DO_EXAMPLES = [
  'Always answer in the language the sender wrote in.',
  'Always address the sender by their first name.',
  'Always end with one clear question.',
  'Always stay under five sentences.',
];

export const DONT_EXAMPLES = [
  'Never name a fixed price.',
  'Never promise a deadline or delivery date.',
  'Never invent facts, numbers or references.',
  'Never mention competitors by name.',
  'Never apologise for reaching out.',
];

/** Response-style starters, offered where the reply instructions are written. */
export const TASK_EXAMPLES = [
  'Keep it under five sentences and end with one clear question.',
  'Match the sender’s tone and language, formal or casual.',
  'Never invent facts. If something is unclear, ask instead.',
  'Refer to a specific detail from their message so it does not read canned.',
];
